/**
 * Der Filter „Mieter" (`open-items-filter-tenant`, Spec 04a, „Reiter Offene
 * Posten"). Dieselbe Suchmechanik wie `TenantPicker`
 * (`features/contracts/TenantPicker.tsx`), hier schmaler und lokal: ein
 * Filter wählt einen bestehenden Mieter, legt nie einen neuen an.
 */

import { useState } from 'react'
import { X } from 'lucide-react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { useTenantOptions } from '@/features/billing/api'
import { FIELD_CLASS } from '@/components/FormField'

export interface TenantFilterValue {
  readonly id: string
  readonly label: string
  /** `true`, solange der Name aus der URL noch nicht bekannt ist — direkt
   * nach dem Öffnen eines geteilten Links (AK-296) steht nur die ID im
   * State, der Name kommt erst mit der Nachladeabfrage. Zeigt einen
   * Platzhalter statt der rohen ID (KD1). */
  readonly isResolving?: boolean
}

interface TenantFilterFieldProps {
  readonly value: TenantFilterValue | null
  readonly onSelect: (tenant: TenantFilterValue) => void
  readonly onClear: () => void
}

export function TenantFilterField({ value, onSelect, onClear }: TenantFilterFieldProps) {
  const [q, setQ] = useState('')
  const optionsQuery = useTenantOptions(q)

  if (value !== null) {
    return (
      <div data-testid="open-items-filter-tenant" className={`flex items-center gap-2 ${FIELD_CLASS}`}>
        {value.isResolving === true ? (
          <span aria-busy="true" aria-live="polite" className="flex h-4 items-center">
            <span className="sr-only">Wird geladen</span>
            <span aria-hidden="true" className="bg-border h-2 w-20 animate-pulse rounded-full" />
          </span>
        ) : (
          <span className="text-body text-text">{value.label}</span>
        )}
        <button
          type="button"
          data-testid="open-items-filter-tenant-clear"
          aria-label="Mieterfilter entfernen"
          onClick={onClear}
          className="text-muted hover:text-text flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center"
        >
          <X className="size-3.5" />
        </button>
      </div>
    )
  }

  return (
    <div data-testid="open-items-filter-tenant" className="relative flex flex-col gap-1">
      <input
        type="search"
        aria-label="Nach Mieter filtern"
        placeholder="Mieter"
        value={q}
        onChange={(event) => {
          setQ(event.target.value)
        }}
        className={`text-text ${FIELD_CLASS}`}
      />

      {q.trim() !== '' && (
        <div className="border-border bg-surface rounded-card absolute top-full z-10 mt-1 w-56 border">
          {optionsQuery.isPending && <SkeletonRows testId="open-items-filter-tenant-loading" rows={2} />}
          {optionsQuery.isError && (
            <ErrorState
              testId="open-items-filter-tenant-error"
              retryTestId="open-items-filter-tenant-retry"
              error={optionsQuery.error}
              onRetry={() => {
                void optionsQuery.refetch()
              }}
            />
          )}
          {optionsQuery.isSuccess && optionsQuery.data.items.length === 0 && (
            <EmptyState testId="open-items-filter-tenant-empty" title="Kein Mieter gefunden." />
          )}
          {optionsQuery.isSuccess && optionsQuery.data.items.length > 0 && (
            <ul>
              {optionsQuery.data.items.map((tenant) => (
                <li key={tenant.id}>
                  <button
                    type="button"
                    data-testid={`open-items-filter-tenant-option-${tenant.id}`}
                    onClick={() => {
                      onSelect({ id: tenant.id, label: tenant.label })
                      setQ('')
                    }}
                    className="hover:bg-bg flex min-h-(--tap-min) w-full items-center px-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
                  >
                    <span className="text-body text-text">{tenant.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
